import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppToaster, useAuth, useBetaStore } from '@cks-internal/react-base';
import { useEffect } from 'react';
import { ErrorContainer } from './ErrorContainer';
import googleAnalyticsService from './features/googleAnalytics/GoogleAnalyticsService';
import { FrontierUser } from './globalModels/frontierUser';
import { FrontierRoutes } from './routes/FrontierRoutes';
import { ChakraRootProvider } from 'src/theme/provider.tsx';
import { pdfjs } from 'react-pdf';
import { useSettings } from './features/hotelSearch/api/useSettings';
import { useOnlineBookingFrontierUserContactsQuery } from 'src/globalModels/api/useOnlineBookingFrontierUserContactsQuery.ts';
import { useProfileStore } from 'src/features/stores/useProfileStore.tsx';

const queryClient = new QueryClient();

useBetaStore.getState().setBeta(true);
useBetaStore.getState().setFlag('selectV2', false);

// Load the worker for react-pdf.js
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs',
//   import.meta.url,
// ).toString();
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const App = () => {
  return (
    <div className='App'>
      <QueryClientProvider client={queryClient}>
        <ChakraRootProvider>
          <ErrorContainer>
            <AppInner />
          </ErrorContainer>
          <AppToaster />
        </ChakraRootProvider>
      </QueryClientProvider>
    </div>
  );
};

// Move hook usage to a nested component where QueryClientProvider is active
const AppInner = () => {
  const { initUser, hasLoaded } = useAuth<FrontierUser>();
  const settingsQuery = useSettings();
  const query = useOnlineBookingFrontierUserContactsQuery();
  const { setUsers, setSelectedUserId } = useProfileStore();

  useEffect(() => {
    if (query.data) {
      setUsers(query.data);

      const storedContactId = Number(localStorage.getItem('selectedContactId'));
      const foundContact = query.data.find((u) => u.ContactId === storedContactId);

      // If the stored contact ID is still valid, use it.
      // Otherwise, fall back to the first contact.
      if (storedContactId && foundContact) {
        setSelectedUserId(storedContactId);
      } else if (query.data.length > 0) {
        const firstContactId = query.data[0].ContactId;
        setSelectedUserId(firstContactId);
        localStorage.setItem('selectedContactId', firstContactId.toString());
      }
    }
  }, [query.data]);
  useEffect(() => {
    async function initAuth() {
      await initUser();
    }

    initAuth();

    if (settingsQuery.data?.GoogleAnalyticsKey) {
      googleAnalyticsService.initializeWithKey(settingsQuery.data.GoogleAnalyticsKey);
    }
  }, [settingsQuery.data]);

  useEffect(() => {
    googleAnalyticsService.recordPageView();
  }, [window.location]);

  return hasLoaded ? <RouterProvider router={FrontierRoutes} /> : null;
};
